// Everything the game needs before the first click: the 3D code (lazy
// chunks), fonts, and the item pictures for the shop and locker (rendered
// once). Loading it all behind the loading screen means opening a table, the
// shop or the locker later does not stall.

/** Give up waiting after this long and show the game anyway (whatever is left loads on demand). */
const PRELOAD_TIMEOUT_MS = 20_000;

interface Step {
  label: string;
  /** Share of the bar. */
  weight: number;
  run: (progress: (fraction: number) => void) => Promise<unknown>;
}

const STEPS: Step[] = [
  {
    label: "Loading the table…",
    weight: 0.3,
    run: () =>
      Promise.all([
        import("../ui/game/GameScreen"),
        import("../three/TableCanvas"),
        import("../ui/game/CharacterView"),
      ]),
  },
  { label: "Loading fonts…", weight: 0.05, run: () => document.fonts?.ready ?? Promise.resolve() },
  {
    label: "Building the room…",
    weight: 0.15,
    run: async () => (await import("../three/warmup")).warmUpScene(),
  },
  {
    label: "Tailoring hats and robes…",
    weight: 0.5,
    run: async (progress) => (await import("../three/thumbnails")).renderThumbnails((done, total) => progress(done / total)),
  },
];

/** Run every step in order, reporting overall progress. Never throws. */
export async function preloadAssets(onProgress: (fraction: number, step: string) => void): Promise<void> {
  const all = (async () => {
    let base = 0;
    for (const step of STEPS) {
      onProgress(base, step.label);
      try {
        await step.run((f) => onProgress(base + step.weight * Math.min(1, f), step.label));
      } catch (e) {
        console.warn(`preload: ${step.label}`, e);
      }
      base += step.weight;
    }
    onProgress(1, "Ready!");
  })();
  await Promise.race([all, new Promise((resolve) => setTimeout(resolve, PRELOAD_TIMEOUT_MS))]);
}
